import React, { useState, useEffect, useRef } from "react";
import { createStory, checkHighlightExists } from "../../functions/story";

export default function CreateStoryModal({ user, onClose, onStoryCreated, onUploadStart, onUploadEnd }) {
  const DURATION_OPTIONS = [
    { value: 1, label: "1 hour" },
    { value: 6, label: "6 hours" },
    { value: 12, label: "12 hours" },
    { value: 24, label: "24 hours" },
    { value: 48, label: "2 days" },
    { value: 72, label: "3 days" },
    { value: 168, label: "7 days" },
  ];

  const [storyType, setStoryType] = useState("text"); // text, image, video
  const [text, setText] = useState("");
  const [backgroundColor, setBackgroundColor] = useState("#1876f2");
  const [font, setFont] = useState("Arial");
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [duration, setDuration] = useState(24); // Default 24 hours
  const [savedToHighlights, setSavedToHighlights] = useState(false);
  const [highlightName, setHighlightName] = useState("");
  const [highlightCover, setHighlightCover] = useState(null);
  const [highlightCoverPreview, setHighlightCoverPreview] = useState(null);
  const [isNewHighlight, setIsNewHighlight] = useState(true);
  const [checkingHighlight, setCheckingHighlight] = useState(false);
  const [highlightNameError, setHighlightNameError] = useState("");
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const textareaRef = useRef(null);
  const isMounted = useRef(true);
  const checkTimeoutRef = useRef(null);

  const backgroundColors = [
    "#1876f2",
    "#42b72a",
    "#e74c3c",
    "#9b59b6",
    "#f39c12",
    "#1abc9c",
    "#34495e",
    "#e67e22",
  ];

  const fonts = [
    "Arial",
    "Helvetica",
    "Times New Roman",
    "Courier New",
    "Verdana",
    "Georgia",
    "Palatino",
    "Garamond",
  ];

  useEffect(() => {
    isMounted.current = true;
    // Block page reload while modal is open (header language reload checks this)
    if (typeof window !== "undefined") window.__storyUploadOrModalInProgress = true;
    return () => {
      isMounted.current = false;
      if (typeof window !== "undefined") window.__storyUploadOrModalInProgress = false;
    };
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Set story type based on file type
      if (file.type.startsWith("video/")) {
        setStoryType("video");
      } else {
        setStoryType("image");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setHighlightNameError("");
    if (savedToHighlights && (!highlightName || !highlightName.trim())) {
      setHighlightNameError("Please enter a name for your memories.");
      return;
    }
    try {
      setLoading(true);
      onUploadStart?.();

      const formData = new FormData();
      if (text) formData.append("text", text);
      if (backgroundColor) formData.append("backgroundColor", backgroundColor);
      if (font) formData.append("font", font);
      formData.append("duration", duration);
      // Convert boolean to string for FormData
      formData.append("savedToHighlights", savedToHighlights ? "true" : "false");
      if (savedToHighlights && highlightName.trim()) {
        formData.append("highlightName", highlightName.trim());
      }
      // Only send cover if it's a new highlight (existing highlights preserve their cover)
      if (savedToHighlights && highlightCover && isNewHighlight) {
        formData.append("highlightCover", highlightCover);
      }
      if (selectedFile) {
        formData.append("media", selectedFile);
      }

      const res = await createStory(formData, user.token);
      if (res && res.story) {
        // Trigger parent with newly created story for optimistic update
        onStoryCreated(res.story);
        onClose();
      } else {
        console.error("Failed to create story:", res?.message || res);
      }
    } catch (err) {
      console.error("Error creating story:", err);
    } finally {
      onUploadEnd?.();
      if (isMounted.current) setLoading(false);
    }
  };

  const resetToText = () => {
    setStoryType("text");
    setSelectedFile(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCoverSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setHighlightCover(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setHighlightCoverPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCover = () => {
    setHighlightCover(null);
    setHighlightCoverPreview(null);
    if (coverInputRef.current) {
      coverInputRef.current.value = "";
    }
  };

  // Check if highlight exists when highlightName changes
  useEffect(() => {
    if (!savedToHighlights || !highlightName || highlightName.trim() === "") {
      setIsNewHighlight(true);
      // Reset cover when highlight is unchecked or name is cleared
      if (!savedToHighlights || highlightName.trim() === "") {
        setHighlightCover(null);
        setHighlightCoverPreview(null);
        if (coverInputRef.current) {
          coverInputRef.current.value = "";
        }
      }
      return;
    }

    // Debounce the check
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }

    setCheckingHighlight(true);
    checkTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await checkHighlightExists(highlightName.trim(), user.token);
        if (res && res.exists !== undefined) {
          const exists = res.exists;
          setIsNewHighlight(!exists);
          // If highlight exists, clear any cover that was selected
          if (exists) {
            setHighlightCover(null);
            setHighlightCoverPreview(null);
            if (coverInputRef.current) {
              coverInputRef.current.value = "";
            }
          }
        }
      } catch (error) {
        console.error("Error checking highlight:", error);
        // Default to showing cover option on error
        setIsNewHighlight(true);
      } finally {
        setCheckingHighlight(false);
      }
    }, 500); // 500ms debounce

    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, [highlightName, savedToHighlights, user.token]);

  return (
    <div className="create_story_modal_overlay">
      <div className="create_story_modal">
        <div className="create_story_header">
          <h2>Create Vibe</h2>
          <button onClick={onClose} className="close_btn">
            ✕
          </button>
        </div>

        <div className="create_story_content">
          {/* Story Type Selector */}
          <div className="story_type_selector">
            <button
              className={`story_type_btn ${
                storyType === "text" ? "active" : ""
              }`}
              onClick={resetToText}
            >
              📝 Text
            </button>
            <button
              className="story_type_btn"
              onClick={() => fileInputRef.current?.click()}
            >
              📷 Photo/Video
            </button>
          </div>

          {/* File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />

          {/* Story Preview */}
          <div className="story_preview">
            {storyType === "text" && (
              <div
                className="story_text_preview"
                style={{
                  backgroundColor: backgroundColor || "#1876f2",
                  fontFamily: font || "Arial",
                  color: "#fff",
                  cursor: "text",
                }}
                onClick={() => textareaRef.current?.focus()}
              >
                {text || "Type your vibe..."}
              </div>
            )}

            {(storyType === "image" || storyType === "video") && preview && (
              <div className="story_media_preview">
                {storyType === "image" ? (
                  <img src={preview} alt="Preview" />
                ) : (
                  <video src={preview} controls />
                )}
                {text && (
                  <div
                    className="story_overlay_text"
                    style={{ fontFamily: font }}
                  >
                    {text}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Story Controls */}
          <div className="story_controls">
            {/* Text Input */}
            <div className="story_text_input">
              <textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="What's on your mind?"
                maxLength={500}
                rows={3}
              />
              <div className="char_count">{text.length}/500</div>
            </div>

            {/* Text Story Options */}
            {storyType === "text" && (
              <>
                {/* Background Colors */}
                <div className="story_options">
                  <label>Background Color:</label>
                  <div className="color_picker">
                    {backgroundColors.map((color) => (
                      <button
                        key={color}
                        className={`color_option ${
                          backgroundColor === color ? "selected" : ""
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => setBackgroundColor(color)}
                      />
                    ))}
                  </div>
                </div>

                {/* Font Selection */}
                <div className="story_options">
                  <label>Font:</label>
                  <select
                    value={font}
                    onChange={(e) => setFont(e.target.value)}
                  >
                    {fonts.map((fontName) => (
                      <option key={fontName} value={fontName}>
                        {fontName}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>

          {/* Duration Selection */}
          <div className="story_options">
            <label>Visibility Duration:</label>
            <select
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              className="duration_select"
            >
              {DURATION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Save to Memories Checkbox */}
          <div className="story_options highlight_checkbox_wrapper">
            <label htmlFor="saveToHighlights" className="highlight_checkbox_label">
              <input
                type="checkbox"
                id="saveToHighlights"
                checked={savedToHighlights}
                onChange={(e) => {
                  setSavedToHighlights(e.target.checked);
                  if (!e.target.checked) setHighlightNameError("");
                }}
                className="highlight_checkbox"
              />
              <span className="highlight_checkbox_text">
                 Save to Memories
              </span>
            </label>
            {savedToHighlights && (
              <>
                <div className="highlight_name_input_wrapper">
                  <label htmlFor="highlightName" className="highlight_name_label">
                    Memories name / Description:
                  </label>
                  <input
                    type="text"
                    id="highlightName"
                    value={highlightName}
                    onChange={(e) => {
                      setHighlightName(e.target.value);
                      if (highlightNameError) setHighlightNameError("");
                    }}
                    placeholder="Enter a name for your memories (e.g., Travel, Food)"
                    maxLength={50}
                    className={`highlight_name_input ${highlightNameError ? "highlight_name_input_error" : ""}`}
                  />
                  {highlightNameError && (
                    <div className="highlight_name_error" role="alert">
                      {highlightNameError}
                    </div>
                  )}
                  <div className="char_count">{highlightName.length}/50</div>
                </div>
                
                {/* Cover Image Upload - Only show for new highlights */}
                {isNewHighlight && !checkingHighlight && (
                  <div className="highlight_cover_upload_wrapper">
                    <label className="highlight_name_label">
                      Cover Image (Optional):
                    </label>
                    <input
                      ref={coverInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleCoverSelect}
                      style={{ display: "none" }}
                    />
                    {highlightCoverPreview ? (
                      <div className="highlight_cover_preview">
                        <img src={highlightCoverPreview} alt="Cover preview" />
                        <button
                          type="button"
                          onClick={removeCover}
                          className="remove_cover_btn"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => coverInputRef.current?.click()}
                        className="upload_cover_btn_small"
                      >
                        📷 Upload Cover Image
                      </button>
                    )}
                    <p className="highlight_cover_helper">
                      Add a custom cover image for your memories. If not provided, the first vibe image will be used.
                    </p>
                  </div>
                )}
                {!isNewHighlight && !checkingHighlight && (
                  <div className="highlight_existing_info">
                    <p className="highlight_cover_helper" style={{ color: "var(--color-secondary)", fontStyle: "italic", marginTop: "8px" }}>
                      This name already exists in Memories. The existing cover will be preserved. You can edit it later from the Memories section.
                    </p>
                  </div>
                )}
                {checkingHighlight && (
                  <div className="highlight_existing_info">
                    <p className="highlight_cover_helper" style={{ color: "var(--color-secondary)", fontStyle: "italic", marginTop: "8px" }}>
                      Checking memories...
                    </p>
                  </div>
                )}
              </>
            )}
            <p className="highlight_helper_text">
              Save this vibe to your profile memories so it stays visible even after expiry.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="story_actions">
            <button onClick={onClose} className="cancel_btn">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || (!text && !selectedFile)}
              className="create_btn"
            >
              {loading ? "Creating..." : "Share Vibe"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
